import { getGuessStatuses } from './statuses'
import { solutionIndex, unicodeSplit } from './words'
import { GameStats } from './localStorage'
import { GAME_TITLE } from '../constants/strings'
import { MAX_CHALLENGES } from '../constants/settings'
import { UAParser } from 'ua-parser-js'

const webShareApiDeviceTypes: string[] = ['mobile', 'smarttv', 'wearable']
const parser = new UAParser()
const browser = parser.getBrowser()
const device = parser.getDevice()

interface Props {
  guesses: string[]
  lost: boolean
  isHardMode: boolean
  isDarkMode: boolean
  isHighContrastMode: boolean
  isDiscord: boolean
  gameStats: GameStats
  handleShareToClipboard: () => void
}

function numberToEmoji(input: number) {
  const emoji = ['0️⃣', '1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣']
  return Array.from(String(input))
    .map((digit) => emoji[Number(digit)])
    .join('')
}

export const shareStatus = ({
  guesses,
  lost,
  isHardMode,
  isDarkMode,
  isHighContrastMode,
  isDiscord,
  gameStats,
  handleShareToClipboard,
}: Props) => {
  const textToShare = `${isDiscord ? ':jmr: ' : ''}${GAME_TITLE} [Day ${
    solutionIndex + 1
  }] ${lost ? 'X' : guesses.length}/${MAX_CHALLENGES}${
    isHardMode ? '*' : ''
  }\n\n${
    isDiscord
      ? generateDiscordEmojiGrid(
          guesses,
          getEmojiTiles(isDarkMode, isHighContrastMode)
        )
      : generateEmojiGrid(
          guesses,
          getEmojiTiles(isDarkMode, isHighContrastMode)
        )
  }\n\n⚡️${numberToEmoji(gameStats.currentStreak)}\n\n${
    isDiscord ? '<' : ''
  }https://fla.sc/morble${isDiscord ? '>' : ''}`

  const shareData = { text: textToShare }

  let shareSuccess = false

  try {
    if (attemptShare(shareData)) {
      navigator.share(shareData)
      shareSuccess = true
    }
  } catch (error) {
    shareSuccess = false
  }

  if (!shareSuccess) {
    navigator.clipboard.writeText(textToShare)
    handleShareToClipboard()
  }
}

export const generateEmojiGrid = (guesses: string[], tiles: string[]) => {
  return guesses
    .map((guess) => {
      const status = getGuessStatuses(guess)
      const splitGuess = unicodeSplit(guess)

      return splitGuess
        .map((_, i) => {
          switch (status[i]) {
            case 'correct':
              return tiles[0]
            case 'present':
              return tiles[1]
            default:
              return tiles[2]
          }
        })
        .join('')
    })
    .join('\n')
}

export const generateDiscordEmojiGrid = (
  guesses: string[],
  tiles: string[]
) => {
  return guesses
    .map((guess, index) => {
      const status = getGuessStatuses(guess)
      const splitGuess = unicodeSplit(guess)

      const outTiles = splitGuess
        .map((_, i) => {
          switch (status[i]) {
            case 'correct':
              return tiles[0]
            case 'present':
              return tiles[1]
            default:
              return tiles[2]
          }
        })
        .join('')
      return `${outTiles}${
        index < guesses.length - 1 ? ` ||\`${guess}\`||` : ''
      }`
    })
    .join('\n')
}

const attemptShare = (shareData: object) => {
  return (
    // Deliberately exclude Firefox Mobile, because its Web Share API isn't working correctly
    browser.name?.toUpperCase().indexOf('FIREFOX') === -1 &&
    webShareApiDeviceTypes.indexOf(device.type ?? '') !== -1 &&
    navigator.canShare &&
    navigator.canShare(shareData) &&
    navigator.share
  )
}

const getEmojiTiles = (isDarkMode: boolean, isHighContrastMode: boolean) => {
  let tiles: string[] = []
  tiles.push(isHighContrastMode ? '🟧' : '🟩')
  tiles.push(isHighContrastMode ? '🟦' : '🟨')
  tiles.push(isDarkMode ? '⬛' : '⬜')
  return tiles
}
